import { useEffect, useState } from "react";
import client, { getErrorMessage } from "@/api/client";
import { useSitesMap } from "@/hooks/useSitesMap";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import EmployeeDetailPanel from "./EmployeeDetailPanel";
import EmployeeFormDialog from "./EmployeeFormDialog";
import { toast } from "sonner";
import { Search, Plus } from "lucide-react";
import { PaginationBar } from "@/components/shared/PaginationBar";

const STATUS_FILTERS = ["All", "Active", "On Leave", "Terminated"];
const MANAGE_ROLES = ["Director", "Manager", "Back Office"];

export default function EmployeeListPage() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [selected, setSelected] = useState(null);
  const [formTarget, setFormTarget] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { sites, siteMap } = useSitesMap();
  const canManage = MANAGE_ROLES.includes(user?.role);

  const load = (pageNum = 1) => {
    setLoading(true);
    const params = { page: pageNum, page_size: 50 };
    if (search) params.search = search;
    if (status !== "All") params.employment_status = status;
    client
      .get("/employees", { params })
      .then((res) => {
        setEmployees(res.data.items);
        setTotal(res.data.total);
        setPage(res.data.page);
        setTotalPages(res.data.total_pages);
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(() => load(1), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status]);

  const handleSaved = (data) => {
    setFormTarget(null);
    setSelected(data);
    load(page);
  };

  return (
    <div className="space-y-6" data-testid="employee-master-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            Employee Master
          </h1>
          <p className="text-sm text-slate-500" data-testid="employee-count">
            {total} employee record{total === 1 ? "" : "s"}
          </p>
        </div>
        {canManage && (
          <Button data-testid="employee-add-button" onClick={() => setFormTarget("new")} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="h-4 w-4 mr-1.5" /> Add Employee
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="employee-search-input"
            placeholder="Search by name, employee ID or mobile"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              data-testid={`employee-status-filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setStatus(s)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                status === s ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="employee-table">
          <TableHeader>
            <TableRow>
              <TableHead>Employee ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Designation</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Location / Site</TableHead>
              <TableHead>App Role</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                  Loading...
                </TableCell>
              </TableRow>
            ) : employees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                  No employees found.
                </TableCell>
              </TableRow>
            ) : (
              employees.map((emp) => (
                <TableRow key={emp.id} data-testid={`employee-row-${emp.employee_id}`} className="cursor-pointer" onClick={() => setSelected(emp)}>
                  <TableCell className="font-mono text-xs font-semibold">{emp.employee_id}</TableCell>
                  <TableCell className="font-medium">{emp.name}</TableCell>
                  <TableCell>{emp.designation}</TableCell>
                  <TableCell>{emp.department || "—"}</TableCell>
                  <TableCell>{siteMap[emp.location_site] || emp.location_site || "—"}</TableCell>
                  <TableCell>{emp.app_role ? <Badge variant="secondary">{emp.app_role}</Badge> : <Badge variant="destructive">Not Set</Badge>}</TableCell>
                  <TableCell>
                    <Badge
                      className={
                        emp.employment_status === "Active"
                          ? "bg-emerald-600"
                          : emp.employment_status === "Terminated"
                            ? "bg-red-600"
                            : "bg-amber-500"
                      }
                    >
                      {emp.employment_status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar page={page} totalPages={totalPages} onPageChange={load} testId="employee-pagination" />

      {selected && (
        <EmployeeDetailPanel
          employee={selected}
          siteMap={siteMap}
          onClose={() => setSelected(null)}
          canManage={canManage}
          onEdit={(emp) => {
            setSelected(null);
            setFormTarget(emp);
          }}
          onUpdated={(updated) => setSelected(updated)}
        />
      )}

      {formTarget && (
        <EmployeeFormDialog employee={formTarget === "new" ? null : formTarget} sites={sites} onClose={() => setFormTarget(null)} onSaved={handleSaved} />
      )}
    </div>
  );
}
